<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";

interface Props {
  isOpen: boolean;
  bookingId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "closeDialog"): void }>();

const isLoading = ref(false);
const historyItems = ref<any[]>([]);

const headers = [
  { title: "Date", key: "created", sortable: false },
  { title: "State", key: "state", sortable: false },
  { title: "Start Time", key: "startTime", sortable: false },
  { title: "Duration", key: "duration", sortable: false },
  { title: "Actual Duration", key: "actualDuration", sortable: false },
  { title: "Area", key: "area", sortable: false },
  { title: "Owner/Users", key: "customer", sortable: false },
  { title: "Edited By", key: "updatedBy", sortable: false },
];

const fetchHistory = async () => {
  if (!props.bookingId) return;
  isLoading.value = true;
  try {
    historyItems.value = await pb.collection("bookingHistory").getFullList({
      filter: `booking = "${props.bookingId}"`,
      sort: "-created",
      expand: "owner,users,area,createdBy,updatedBy",
    });
  } catch (e) {
    console.error("Error fetching booking history:", e);
    historyItems.value = [];
  } finally {
    isLoading.value = false;
  }
};

const userName = (item: any) => {
  if (item.visitors) return item.visitors;
  const user = item.expand?.users?.[0] ?? item.expand?.owner;
  if (user) return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return "—";
};

const editedByName = (item: any) => {
  const u = item.expand?.updatedBy ?? item.expand?.createdBy;
  if (!u) return "—";
  return `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.username || "—";
};

watch(() => [props.isOpen, props.bookingId], ([open]) => {
  if (open) fetchHistory();
}, { immediate: true });
</script>

<template>
  <VDialog :model-value="isOpen" max-width="950" scrollable @update:model-value="emit('closeDialog')">
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between pt-2 px-4">
        <span>Booking History</span>
        <IconBtn icon="mdi-close" @click="emit('closeDialog')" />
      </VCardTitle>

      <VDivider />

      <VCardText class="pa-0 mb-4">
        <VDataTable
          :loading="isLoading"
          :items="historyItems"
          :headers="headers"
          density="compact"
          hide-default-footer
          :items-per-page="-1"
        >
          <template #item.created="{ item }">
            <span class="text-sm">{{ moment(item.created).format("DD/MM/YY h:mma") }}</span>
          </template>

          <template #item.state="{ item }">
            <VChip
              size="small"
              :color="item.state === 'Deleted' ? 'error' : item.state === 'Confirmed' ? 'success' : ''"
            >
              {{ item.state || "—" }}
            </VChip>
          </template>

          <template #item.startTime="{ item }">
            <span class="text-sm" v-if="item.startTime">
              {{ moment(item.startTime).format("DD/MM/YY h:mma") }}
            </span>
            <span v-else>—</span>
          </template>

          <template #item.duration="{ item }">
            {{ item.duration ? `${item.duration} min` : "—" }}
          </template>

          <template #item.actualDuration="{ item }">
            {{ item.actualDuration ? `${item.actualDuration} min` : "—" }}
          </template>

          <template #item.area="{ item }">
            {{ item.expand?.area?.areaNumber ?? "—" }}
          </template>

          <template #item.customer="{ item }">
            {{ userName(item) }}
          </template>

          <template #item.updatedBy="{ item }">
            {{ editedByName(item) }}
          </template>

          <template #no-data>
            <div class="text-center py-6 text-medium-emphasis">No history found for this booking.</div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>
  </VDialog>
</template>
